import ArticleLayout, { P, H2, UL, LI, Strong, Note } from '../../components/ArticleLayout.jsx';

export default function ProteinBasics({ related }) {
  return (
    <ArticleLayout
      title="How much protein do you actually need?"
      summary="The internet says everything from 0.4 to 2.0 grams per pound. The research says a much narrower range — and the answer depends on what you're trying to do, not how big you want to be."
      readingMin={6}
      date="2026-05-23"
      related={related}
    >
      <P>
        If you've spent five minutes reading fitness content, you've seen wildly different protein recommendations. Government guidelines say 0.36 g/lb is enough to avoid deficiency. Bodybuilding forums insist you need 1.5+ g/lb to build muscle. Both are technically true and practically wrong — the right answer for someone training hard is between those numbers, and the goal you're chasing matters more than the absolute amount.
      </P>

      <H2>The research-backed ranges</H2>
      <P>
        Decades of studies on protein intake for lifters converge on a fairly narrow window. Beyond the top of these ranges, additional protein produces no additional muscle gain — it just becomes expensive urine.
      </P>
      <UL>
        <LI><Strong>Sedentary adult, no training goal:</Strong> 0.36–0.5 g per pound of body weight. Enough to prevent deficiency, support normal body function.</LI>
        <LI><Strong>Recreational lifter, maintaining weight:</Strong> 0.7–0.9 g/lb. Supports recovery and modest muscle growth.</LI>
        <LI><Strong>Lifter trying to build muscle:</Strong> 0.8–1.0 g/lb. Maximizes muscle protein synthesis when training stimulus is sufficient.</LI>
        <LI><Strong>Lifter in a calorie deficit (cutting):</Strong> 1.1–1.3 g/lb. Higher protein protects muscle when calories are low.</LI>
        <LI><Strong>Athlete in heavy training + cut:</Strong> 1.3–1.5 g/lb. The legitimate upper end. Above this, no extra benefit.</LI>
      </UL>
      <Note>
        TrainForge applies this automatically: 0.8 g/lb for general health and endurance goals, 1.0 g/lb for muscle/strength, 1.1 g/lb for recomp, and 1.2 g/lb for fat loss.
      </Note>

      <H2>Why cutting raises the protein number</H2>
      <P>
        When you eat below maintenance, your body needs energy and starts pulling it from somewhere. The dial it can turn is the ratio of fat vs muscle it breaks down. High protein intake, combined with hard resistance training, tells the body to preserve muscle and pull more from fat. Low protein in a deficit means more of the weight you lose is muscle — which is the opposite of what you want.
      </P>
      <P>
        This is why a 1.2 g/lb target during a cut isn't about building extra muscle (you can't really build muscle in a meaningful deficit). It's about protecting what you have.
      </P>

      <H2>Distribution matters more than people think</H2>
      <P>
        Hitting your daily protein target in one giant 200g meal is significantly less effective for muscle growth than spreading it across 3–5 meals. The reason: muscle protein synthesis (MPS) is a switch with a refractory period. Each protein-containing meal turns it on for ~3–4 hours; eating more protein during that window doesn't increase the response.
      </P>
      <UL>
        <LI><Strong>Optimal pattern:</Strong> 4 meals, 30–50g protein each, spaced 3–5 hours apart.</LI>
        <LI><Strong>Minimum effective dose per meal:</Strong> ~25–30g of complete protein (about 4 oz of chicken or fish, or 1 scoop of whey).</LI>
        <LI><Strong>Pre-bed protein:</Strong> A slow-digesting protein (cottage cheese, casein, Greek yogurt) before sleep gives a longer overnight MPS window than fast proteins.</LI>
      </UL>

      <H2>The myths worth busting</H2>
      <UL>
        <LI><Strong>"Your body can only absorb 30g of protein at a time."</Strong> False. Your body absorbs all of it. What's capped at ~30g per meal is the muscle-building <em>signal</em>, not absorption. Excess protein still serves other body functions or gets oxidized for energy.</LI>
        <LI><Strong>"High protein damages your kidneys."</Strong> No evidence in people with healthy kidneys. The myth comes from advice given to people with existing kidney disease, where protein management matters. Healthy adults eating 1+ g/lb show no kidney harm in long-term studies.</LI>
        <LI><Strong>"Plant protein doesn't build muscle."</Strong> It does, but the per-gram efficiency is slightly lower because plant proteins are often incomplete. Plant-based lifters typically need to eat 10–20% more total protein to match the muscle-building effect of animal protein.</LI>
        <LI><Strong>"Protein timing around workouts is critical."</Strong> Overrated. The "anabolic window" of 30 minutes post-workout is more like 4–6 hours. As long as you've had a protein meal within a few hours of training, timing doesn't make a meaningful difference.</LI>
      </UL>

      <H2>What about leucine?</H2>
      <P>
        Of the 20 amino acids in protein, leucine is the one that triggers muscle protein synthesis. Each meal needs ~2.5–3g of leucine to maximally activate MPS — which works out to roughly 25–30g of complete protein. This is why "complete protein per meal" matters more than "total daily protein." Hitting 200g of protein but distributed as five 40g servings of plant protein with poor leucine content can underperform a smaller daily total of well-distributed animal protein.
      </P>

      <H2>Practical food amounts</H2>
      <UL>
        <LI>4 oz chicken breast: 30g protein</LI>
        <LI>4 oz lean ground beef (90/10): 22g</LI>
        <LI>4 oz salmon: 25g</LI>
        <LI>1 scoop whey protein: 24–28g</LI>
        <LI>3 large eggs: 18g</LI>
        <LI>1 cup Greek yogurt (plain, 0% fat): 22g</LI>
        <LI>1 cup cottage cheese (low-fat): 25g</LI>
        <LI>1 cup cooked lentils: 18g</LI>
        <LI>1 cup tofu (firm): 20g</LI>
      </UL>

      <H2>Should I hit my protein number every single day?</H2>
      <P>
        Not perfectly. Aim for your target on average across the week. A 30% miss on one day doesn't hurt anything if the other six days are on track. Chasing perfect daily numbers leads to obsession and binge cycles — and the muscle adaptation responds to weekly averages, not single days.
      </P>
    </ArticleLayout>
  );
}
